import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import { RootState } from '../../store';
import { Iam } from "@smash-sdk/iam/ejs/10-2019";
import { AsyncState } from '../../shared/models/async.interface';
import { removeEmptyFields } from '../../shared/helpers/helpers';
import { UpdateAccountInput } from '@smash-sdk/iam/ejs/07-2021';

export interface ProfileInformation {
    id: string;
    username: string;
    firstName: string;
    lastName: string;
    region: string;
    language: string;
    created: string;
    modified: string;
}


export interface ProfileInformationsState extends AsyncState {
    profile: ProfileInformation | null;
    isUpdated: boolean;
}


export const getProfileInformation = createAsyncThunk(
    'profileInformations/getProfileInformation',
    async (_, thunkApi) => {
        try {
            const iam = new Iam();
            const { account } = await iam.getAccount();
            return { profileInformations: account };
        } catch (error: any) {
            return thunkApi.rejectWithValue({ ...error });
        }
    }
)

export const updateProfileInformation = createAsyncThunk(
    'profileInformations/updateProfileInformation',
    async (profileInformations: UpdateAccountInput, thunkApi) => {
        try {
            const iam = new Iam({});
            const { account } = await iam.updateAccount(removeEmptyFields(profileInformations));
            return { profileInformations: account };
        } catch (error: any) {
            return thunkApi.rejectWithValue({ ...error });
        }
    }
)


export const initialState: ProfileInformationsState = {
    profile: null,
    isSuccess: false,
    isLoading: false,
    isError: false,
    isUpdated: false,
}

export const profileInformationsSlice = createSlice({
    name: 'profileInformations',
    initialState,
    reducers: {
        resetProfileInformations: (state) => ({
            profile: null,
            isLoading: false,
            isSuccess: false,
            isError: false,
            isUpdated: false,
        }),
        resetProfileUpdated: (state) => ({
            ...state,
            isUpdated: false,
        }),
    },
    extraReducers: (builder) => {
        builder
            .addCase(updateProfileInformation.pending, (state) => ({
                ...state,
                isUpdated: false,
                isSuccess: false,
                isLoading: true,
                isError: false,
            }))
            .addCase(updateProfileInformation.fulfilled, (state, action) => ({
                ...state,
                profile: action.payload.profileInformations,
                isUpdated: true,
                isSuccess: true,
                isLoading: false,
                isError: false,
            }))
            .addCase(updateProfileInformation.rejected, (state) => ({
                ...state,
                isUpdated: false,
                isSuccess: false,
                isLoading: false,
                isError: true,
            }))
            .addCase(getProfileInformation.pending, (state) => ({
                ...state,
                isSuccess: false,
                isLoading: true,
                isError: false,
            }))
            .addCase(getProfileInformation.fulfilled, (state, action) => ({
                ...state,
                profile: action.payload.profileInformations,
                isSuccess: true,
                isLoading: false,
                isError: false,
            }))
            .addCase(getProfileInformation.rejected, (state) => ({
                ...state,
                isSuccess: false,
                isLoading: false,
                isError: true,
            }))
    }
});


export const { resetProfileInformations, resetProfileUpdated } = profileInformationsSlice.actions;
export const profileInformationsState = (state: RootState) => state.profileInformations;

export default profileInformationsSlice.reducer;